import { HttpErrorResponse, HttpEventType, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { AppError, AppErrorBuilder, isUserFacingErrorExtensions, TranslatableError } from '@h5s/util-error';
import { Logger, LoggerService } from '@h5s/util-logger';
import { safeStringify } from '@h5s/util-string';
import { catchError, from, map, mergeMap, Observable, of, throwError } from 'rxjs';
import { ErrorService } from './error.service';

const INTERCEPTOR_NAME = 'httpErrorInterceptor';

export const httpErrorInterceptor: HttpInterceptorFn = (request, next) => {
  const loggerService = inject(LoggerService);
  const errorService = inject(ErrorService);

  const logger = loggerService.getLogger(INTERCEPTOR_NAME);
  logger.debug(`HTTP interceptor request`, request);

  return next(request).pipe(
    catchError((err: HttpErrorResponse) => {
      logger.error(`[${err.url}] HTTP error <${HttpEventType[err.type]}>: ${err.message}`, { err });

      return handleError(err, request, logger, errorService).pipe(mergeMap((appError) => throwError(() => appError)));
    }),
  );
};

function handleError(
  error: HttpErrorResponse,
  request: HttpRequest<unknown>,
  logger: Logger,
  errorService: ErrorService,
): Observable<AppError> {
  if (error.error instanceof ErrorEvent) {
    // È avvenuto un errore client-side
    logger.error(
      `Client side error in file ${error.error.filename} ${error.error.lineno}:${error.error.colno}: ${error.error.message}`,
    );
    return of(manageClientError(error));
  } else {
    // È avvenuto un errore server-side, il backend ci ritorna quindi un errore da interpretare
    logger.error(`Server side error with code ${error.status} and body, `, error.error);
    return manageHttpResponseError(error, request, errorService);
  }
}

/**
 * Gestisce un errore lato client (error.error = instanceof ErrorEvent). Crea un oggetto eccezione aggiungendo tutti i
 * dettagli disponibili.
 *
 * @param {HttpErrorResponse} err - L'oggetto HttpErrorResponse da cui costruire l'eccezione.
 * @return {AppError} - L'oggetto AppError appena creato.
 */
function manageClientError(err: HttpErrorResponse): AppError {
  return new AppErrorBuilder(`${err.error.name}: ${err.error.message}.`)
    .addCause(err)
    .addInfo('code', 'HTTP_CLIENT_OR_NETWORK_ERROR')
    .addInfo(
      'description',
      'Si è verificato un errore durante la richiesta, verificare la connessione di rete e riprovare',
    )
    .build();
}

/**
 * Gestisce un errore HTTP. Crea un oggetto eccezione aggiungendo tutti i dettagli disponibili.
 *
 * @param {HttpErrorResponse} err - L'oggetto HttpErrorResponse da cui costruire l'eccezione.
 * @return {AppError} - L'oggetto AppError appena creato.
 */
function manageHttpResponseError(
  err: HttpErrorResponse,
  request: HttpRequest<unknown>,
  errorService: ErrorService,
): Observable<AppError> {
  if (err.error instanceof Blob && err.error.type === 'application/json') {
    return from(err.error.text()).pipe(
      map((jsonText) => {
        const json = JSON.parse(jsonText);

        // In caso di errore di interazione utente
        if (isUserFacingErrorExtensions(json)) {
          const translatableError: TranslatableError = {
            translationKey: json.translationKey,
            arguments: Array.isArray(json.arguments) ? json.arguments : [],
          };

          errorService.translateAndDisplayErrors([translatableError]);
        }

        // In caso non sia un UserFacingError, non serve loggarlo, in quanto viene già loggato dal GlobalErrorHandler

        const appError = buildHttpResponseError(err, request);
        // Se err.error è un Blob sovrascriviamo responseBody con il testo effettivo già letto dal Blob.
        appError.info.set('responseBody', jsonText);
        return appError;
      }),
    );
  } else {
    return of(buildHttpResponseError(err, request));
  }
}

/**
 * Costruisce un oggetto AppError a partire da un HttpErrorResponse e una HttpRequest.
 *
 * @param {HttpErrorResponse} err - L'oggetto HttpErrorResponse da cui costruire l'eccezione.
 * @param {HttpRequest<unknown>} request - L'oggetto HttpRequest associato all'errore.
 * @returns {AppError} - L'oggetto AppError appena creato.
 */
function buildHttpResponseError(err: HttpErrorResponse, request: HttpRequest<unknown>): AppError {
  const httpErrorBuilder = new AppErrorBuilder(`${err.name}: ${err.message}.`).addCause(err);

  httpErrorBuilder.addInfo('requestMethod', request.method);
  httpErrorBuilder.addInfo('requestUrl', request.urlWithParams);

  if (request.body != null) {
    httpErrorBuilder.addInfo('requestBody', safeStringify(request.body));
  }

  if (err.error != null) {
    httpErrorBuilder.addInfo('responseBody', safeStringify(err.error));
  }

  return httpErrorBuilder.build();
}
